import { api } from "../utils/api";
import { ModelParams, SolveFor } from "../OptionSolver";
import { useLiveMarketDataStore, useOrderModifyDataStore, useTradesStore, useUnderlyingOrderModifyDataStore } from "./store";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { INCREMENT_PRESETS, QuickPicks } from "./OrderInput";

export const UnderlyingOrder = ({
  modifyOrCreateUnderlyingOrder,
}: {
  modifyOrCreateUnderlyingOrder: (e: React.FormEvent) => void;
}) => {
  const underlyingOrderDataStore = useUnderlyingOrderModifyDataStore();
  const liveMarketDataStore = useLiveMarketDataStore();
  const tradesStore = useTradesStore();
  const currentOrderStatus = tradesStore.trades.find(
    (t) => t.orderId.toString() === underlyingOrderDataStore.underlyingOrderModifyData.orderId
  )?.status;
  const [underlyingData, setUnderlyingData] = useState<string>("");
  const [increment, setIncrement] = useState<string>("0.01");
  const underlyingQuote = liveMarketDataStore.liveMarketData.find((d) => d.readable_name.length <= 6);

  useEffect(() => {
    underlyingOrderDataStore.setAction("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <form onSubmit={modifyOrCreateUnderlyingOrder} className="flex gap-4 items-center pb-6">
        {underlyingOrderDataStore.underlyingOrderModifyData.orderId !== "" ? (
          <div className="flex flex-row gap-4">
            <div>
              <label className="block" htmlFor="order-id">Editing id:</label>
              <input
                id="order-id"
                type="text"
                disabled
                className="bg-slate-200"
                value={underlyingOrderDataStore.underlyingOrderModifyData.orderId}
              />
            </div>
            <div>
              <label>Action:</label>
              <div
                className={`font-bold ${
                  underlyingOrderDataStore.underlyingOrderModifyData.action === "BUY"
                    ? "text-blue-600"
                    : underlyingOrderDataStore.underlyingOrderModifyData.action === "SELL"
                    ? "text-orange-500"
                    : "text-gray-400"
                }`}
              >
                {underlyingOrderDataStore.underlyingOrderModifyData.action || "—"}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-row gap-4">
            <div>
              <label htmlFor="order-id">New order:</label>
              <input
                id="order-id"
                type="text"
                disabled
                className="bg-slate-200"
                value={underlyingQuote?.readable_name.toUpperCase() ?? ""}
              />
            </div>

            <div>
              <label>Action:</label>
              <div
                className={`font-bold ${
                  underlyingOrderDataStore.underlyingOrderModifyData.action === "BUY"
                    ? "text-blue-600"
                    : underlyingOrderDataStore.underlyingOrderModifyData.action === "SELL"
                    ? "text-orange-500"
                    : "text-gray-400"
                }`}
              >
                {underlyingOrderDataStore.underlyingOrderModifyData.action || "—"}
              </div>
            </div>



        <div>
          <label>Action:</label>
          <div className="flex-col gap-1">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="underlying-action"
                value="BUY"
                checked={underlyingOrderDataStore.underlyingOrderModifyData.action === "BUY"}
                onChange={() => underlyingOrderDataStore.setAction("BUY")}
              />
              <span className="text-blue-600 font-bold">BUY</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="underlying-action"
                value="SELL"
                checked={underlyingOrderDataStore.underlyingOrderModifyData.action === "SELL"}
                onChange={() => underlyingOrderDataStore.setAction("SELL")}
              />
              <span className="text-orange-500 font-bold">SELL</span>
            </label>
          </div>
        </div>
          </div>
        )}


        <div className="w-36 relative">
          <label htmlFor="price">Price:</label>
          <input
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            results={0}
            name="custom_field_price"
            step="any"
            id="underlying-price"
            type="number"
            value={underlyingOrderDataStore.underlyingOrderModifyData.price}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            onChange={(e) => {
              // only update if the input is in focus (user is typing)
              if (document.activeElement?.id !== "underlying-price") {
                return;
              }

              underlyingOrderDataStore.setPrice(e.target.value);
            }}
            onKeyDown={(e) => {
              // step="any" (so arbitrary clicked/typed prices stay valid)
              // means the browser's own Up/Down stepping defaults to 1;
              // nudge by the Increment field ourselves instead.
              if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
              e.preventDefault();

              const inc = parseFloat(increment);
              const step = !isNaN(inc) && inc > 0 ? inc : 0.01;
              const current =
                parseFloat(underlyingOrderDataStore.underlyingOrderModifyData.price) || 0;
              const next =
                e.key === "ArrowUp" ? current + step : current - step;

              underlyingOrderDataStore.setPrice(Math.max(0, next).toFixed(2));
            }}
          />
          <QuickPicks
            items={(["bid", "ask"] as const).map((side) => {
              const quote = underlyingQuote?.[side];
              return {
                label: side.toUpperCase(),
                title: quote !== undefined ? quote.toFixed(2) : "No live quote",
                onPick: () => {
                  if (quote === undefined) return;
                  underlyingOrderDataStore.setPrice(quote.toFixed(2));
                },
              };
            })}
          />
        </div>

        <div className="w-24 relative">
          <label htmlFor="increment">Increment:</label>
          <input
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            results={0}
            name="custom_field_increment"
            step={0.01}
            id="underlying-increment"
            type="number"
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            value={increment}
            onChange={(e) => {
              // only update if the input is in focus (user is typing)
              if (document.activeElement?.id !== "underlying-increment") {
                return;
              }

              setIncrement(e.target.value);
            }}
          />
          <QuickPicks
            padOneSide
            items={INCREMENT_PRESETS.map(([label, value]) => ({
              label,
              title: value,
              onPick: () => setIncrement(value),
            }))}
          />
        </div>
        
        <div className="w-36 relative">
          <label htmlFor="size">Size:</label>
          <input
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            results={0}
            name="custom_field_size"
            id="underlying-size"
            type="number"
            step={1}
            value={underlyingOrderDataStore.underlyingOrderModifyData.size}
            onChange={(e) => {
              // only update if the input is in focus (user is typing)
              if (document.activeElement?.id !== "underlying-size") {
                return;
              }

              underlyingOrderDataStore.setSize(e.target.value);
            }}
          />
          <QuickPicks
            padOneSide
            items={[-10, -5, 5, 10].map((delta) => ({
              label: delta > 0 ? `+${delta}` : `${delta}`,
              title: `${delta > 0 ? "Add" : "Subtract"} ${Math.abs(delta)}`,
              onPick: () => {
                const current = parseInt(underlyingOrderDataStore.underlyingOrderModifyData.size, 10) || 0;
                underlyingOrderDataStore.setSize(Math.max(0, current + delta).toString());
              },
            }))}
          />
        </div>

        <div className="w-40">
          <label htmlFor="total">Total:</label>
          <input
            className="bg-slate-200"
            id="total"
            type="text"
            value={underlyingOrderDataStore.underlyingOrderModifyData.total}
            disabled
          />
        </div>

        {underlyingOrderDataStore.underlyingOrderModifyData.orderId !== "" && (
          <button
            type="button"
            className="btn btn-secondary mt-5"
            onClick={() => underlyingOrderDataStore.reset()}
          >
            Discard
          </button>
        )}
        {currentOrderStatus === "Filled" ? (
          <button type="button" className="btn btn-success mt-5" disabled>Filled</button>
        ) : currentOrderStatus === "Cancelled" ? (
          <button type="button" className="btn btn-danger mt-5" disabled>Cancelled</button>
        ) : (
          <button
            type="submit"
            className="btn btn-default mt-5"
            disabled={
              underlyingOrderDataStore.underlyingOrderModifyData.orderId === "" &&
              underlyingOrderDataStore.underlyingOrderModifyData.action === ""
            }
          >
            {underlyingOrderDataStore.underlyingOrderModifyData.orderId === "" ? "Create" : "Modify"}
          </button>
        )}
      </form>

      <div>{underlyingData}</div>
    </div>
  );
};
